import { queryKeys } from '@query/keys'
import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { useAccountReadContext } from '@app/context/AccountContextContext'

import { useSessionLanguage } from '@context/SessionSettingsContext'

import { SummaryItem } from '@ui/v2/TableSummary/TableSummary'

import { useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'

import { FilterQueryProps } from '@model/FilterFields'

interface BrokerSummaryFetchProps {
  search: SearchResponseProps
}

interface SearchResponseProps {
  search: FilterQueryProps
  debouncedSearchTerm: string | null
}

export const useIBClientJourneyMapSummary = (props: BrokerSummaryFetchProps) => {
  const apiClient = useApiClient(ClientApiClient)
  const locale = useSessionLanguage()
  const { t } = useTranslation()
  const { account } = useAccountReadContext()
  const { search } = props

  const ibId = account?.clientIntroducingBroker?.id

  const searchKey = useMemo(
    () => ({
      ...search.search,
      ca_search_Name: search.debouncedSearchTerm,
      locale,
    }),
    [search.search, search.debouncedSearchTerm, locale]
  )

  const { data, isLoading } = useQuery({
    queryKey: queryKeys.ib.cjmSummary(ibId, searchKey),
    queryFn: () =>
      apiClient.getClientJourneyMapSummary(ibId!, {
        search: { ...search.search, ca_search_Name: search.debouncedSearchTerm },
        pageIndex: 1,
        caller: 'ca',
        languageId: locale,
      }),
    enabled: !!ibId,
  })

  const summaryItems = useMemo<SummaryItem[]>(() => {
    const totalString = `/ ${data?.total ?? '0'}`
    return [
      {
        title: t('IB.Dashboard.CJM.Emails verified'),
        value: `${data?.emailVerified ?? 0} ${totalString}`,
      },
      {
        title: t('IB.Dashboard.CJM.activated'),
        value: `${data?.activated ?? 0} ${totalString}`,
      },
      {
        title: t('IB.Dashboard.CJM.deposited'),
        value: `${data?.deposited ?? 0} ${totalString}`,
      },
      {
        title: t('IB.Dashboard.CJM.traded'),
        value: `${data?.traded ?? 0} ${totalString}`,
      },
    ]
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, t])

  return { summaryItems, isLoading }
}
